import React, { useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

/**
 * The chrome of the expanded ("lightbox") preview: the backdrop, the close
 * control, and the keyboard/scroll behaviour a full-viewport overlay owes a
 * user. Phase 6 of `docs/plans/manuscript-file-delivery.md`.
 *
 * ⚠ THE PREVIEW ELEMENT IS DELIBERATELY NOT A CHILD OF THIS COMPONENT, AND
 * MUST NEVER BECOME ONE. Expanding a preview is a CSS state change on the
 * already-mounted `<img>` / `<iframe>` — see `expandedPreviewImageSx` and
 * `expandedPreviewFrameSx` below, which `MediaDeliveryPanel` swaps in place of
 * the inline styles. The reason is not taste:
 *
 * - An `<iframe>` begins a fresh load whenever it is inserted into a document.
 *   Moving the preview into a `Dialog`/`Modal`/portal reparents the node, so
 *   the PDF re-streams and loses its page position on every expand — which is
 *   exactly what the operator asked us to stop doing.
 * - Rendering a *second* element for the expanded view means two concurrent
 *   streams of one file, both counting against `stream_max_concurrent`
 *   (default 4 per uvicorn worker).
 *
 * The backdrop has no such constraint — it holds no state and costs nothing to
 * re-create — so it lives here as an ordinary fixed-position sibling.
 *
 * ⚠ SECOND CONSTRAINT, INVISIBLE FROM THIS FILE: `position: fixed` resolves
 * against the viewport only while NO ancestor of the preview element has a
 * `transform`, `filter`, `will-change` or `contain` — any of those makes the
 * ancestor the containing block. Nothing under `pages/chatPreview/` uses one
 * today (checked 2026-08-24, including the MUI transition components, which
 * are not used on this page). If someone adds one to a bubble, a panel or the
 * message list, this silently breaks and the symptom — a "full-screen" overlay
 * confined to a chat bubble — looks nothing like the cause.
 */

/**
 * Height of the strip the close control sits in. The preview element starts
 * below it, so the control never lands on top of the browser's own PDF toolbar
 * and the strip itself stays clickable backdrop.
 */
const BAR_PX = 48;

/** The expanded `<img>`: centred at its own size, so the backdrop around it stays clickable. */
export const expandedPreviewImageSx: SxProps<Theme> = {
  position: 'fixed',
  top: `${BAR_PX}px`,
  bottom: 0,
  insetInline: 0,
  m: 'auto',
  width: 'auto',
  height: 'auto',
  maxWidth: '100%',
  maxHeight: `calc(100% - ${BAR_PX}px)`,
  borderRadius: 0,
  zIndex: (theme) => theme.zIndex.modal + 1,
};

/** The expanded `<iframe>`: fills what the bar leaves, because a PDF viewer wants every pixel. */
export const expandedPreviewFrameSx: SxProps<Theme> = {
  position: 'fixed',
  top: `${BAR_PX}px`,
  bottom: 0,
  insetInline: 0,
  m: 0,
  width: '100%',
  height: `calc(100% - ${BAR_PX}px)`,
  border: 0,
  borderRadius: 0,
  bgcolor: 'rgba(255,255,255,0.98)',
  zIndex: (theme) => theme.zIndex.modal + 1,
};

interface MediaPreviewOverlayProps {
  open: boolean;
  /** Must be referentially stable — the keyboard handlers are bound on it. */
  onClose: () => void;
  /** `aria-label` for the dialog. With `aria-modal`, the preview element itself
   *  is outside the dialog and therefore hidden from AT, so this carries the
   *  file name that the `alt`/`title` would otherwise have supplied. */
  label: string;
  closeLabel: string;
  /** The already-mounted preview element. It is allowed to hold focus; nothing
   *  else outside the close control is. */
  previewRef: React.RefObject<HTMLElement | null>;
  /** Focused again when the overlay closes — the control that opened it. */
  returnFocusRef: React.RefObject<HTMLElement | null>;
}

const MediaPreviewOverlay: React.FC<MediaPreviewOverlayProps> = ({
  open,
  onClose,
  label,
  closeLabel,
  previewRef,
  returnFocusRef,
}) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Body scroll lock. The cleanup runs on close AND on unmount, which is the
  // case that matters: a bubble can unmount while expanded (the reviewer
  // navigates, the message list re-renders), and leaving `overflow: hidden`
  // behind makes the whole page unscrollable with nothing on screen to blame.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  // Escape closes, and Tab cannot walk out of the overlay into the card behind
  // the backdrop. There is exactly one focus stop in here — the close control —
  // so the trap is "put focus back", not a tab-order cycle.
  //
  // Known limit, stated rather than hidden: once focus is INSIDE the PDF
  // iframe, that document is sandboxed and same-origin-opaque, so its key
  // events never reach us and Escape stops working. The close control stays
  // visible and clickable, which is why it is not overlaid on the frame.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      event.preventDefault();
      closeRef.current?.focus();
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (closeRef.current?.contains(target)) return;
      if (previewRef.current === target || previewRef.current?.contains(target)) return;
      closeRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, [open, onClose, previewRef]);

  useEffect(() => { if (open) closeRef.current?.focus(); }, [open]);

  // Focus returns to the control that opened the overlay. The target is read
  // when the overlay opens, not in the cleanup: by cleanup time the panel may
  // have re-rendered the button away. On unmount the node may already be
  // detached; `focus()` on a detached node is a no-op, not a throw.
  useEffect(() => {
    if (!open) return undefined;
    const returnTo = returnFocusRef.current;
    return () => { returnTo?.focus(); };
  }, [open, returnFocusRef]);

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={label}
      // Every handler here stops propagation: the card underneath is a
      // `role="button"` toggle region, and a click that reaches it would
      // collapse the card out from under the overlay.
      onClick={(event: React.MouseEvent) => { event.stopPropagation(); onClose(); }}
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.88)',
        overscrollBehavior: 'contain',
        zIndex: (theme) => theme.zIndex.modal,
      }}
    >
      <IconButton
        ref={closeRef}
        aria-label={closeLabel}
        title={closeLabel}
        onClick={(event: React.MouseEvent) => { event.stopPropagation(); onClose(); }}
        sx={{
          position: 'absolute',
          top: '4px',
          // Logical, not `right`: these pages flip to RTL with the language and
          // there is no stylis RTL plugin in this app to flip a physical side.
          insetInlineEnd: '8px',
          color: 'common.white',
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default MediaPreviewOverlay;
